import { ORDER } from '../core/systems.js';
import { TEAM, ST, KIND } from '../core/entities.js';
import { ACT } from '../ai/store.js';

/**
 * ===========================================================================
 *  DUEL — the officer fight, staged
 * ===========================================================================
 *
 * DEV_PLAN Step 6. Like Step 5, most of this was already built and nobody had
 * called it:
 *
 *   - `world.ai.engaged` is already "the nearest officer who is targeting the
 *     player and is inside 16 m" — an officer ENCOUNTER, maintained every tick.
 *   - `ACT.RING` is already "orbit the target at `ringR` and wait for a token",
 *     which is a ring, formed by the AI's own steering.
 *   - `ACT.DUEL` already exists and `grunt.js` already refuses to re-task a
 *     unit that is in it.
 *   - `CINEMATICS.duel` already exists — a 1.6 s arc-in — and was unused.
 *
 * So this module is a state machine over pieces that were there, plus a prompt.
 *
 * THE RING IS ALSO THE SUPPRESSION, which is the part worth knowing. A grunt in
 * `ACT.RING` only asks for an attack token when it is inside 3.5 m
 * (`grunt.js`), so widening `ringR` from its spawn value of about 2.3-3.0 m to
 * `RING_RADIUS` pushes the crowd out of its own reach. Nothing needs to disable
 * their attacks: they simply cannot make one from where they are standing. That
 * is one field per unit, restored when the duel ends, and no edit to `src/ai/*`
 * at all. (`world.ai.tokens` would have been the obvious lever and it is the
 * wrong one — its setter clamps to a minimum of 1, so it can never mean "no
 * one swings".)
 *
 * `ringR` is safe to write because the AI sets it once at spawn and never
 * again — `store.js` at creation, `ai.js` for archers. A per-tick write would
 * have made this approach impossible.
 */

/** An engaged officer inside this range offers a duel. */
export const PROMPT_RANGE = 12;
/** Enemy grunts inside this join the ring when the duel starts. */
export const GATHER_RANGE = 16;
/** Where the ring stands. Past the 3.5 m an orbiting grunt attacks from. */
export const RING_RADIUS = 7.5;
/** How close to `RING_RADIUS` counts as "in the ring". */
export const RING_TOLERANCE = 2.2;
/** How many men make a ring rather than a few bystanders. */
export const RING_MIN = 4;
/**
 * And how many make a ring rather than a WALL.
 *
 * The contract test pulled 49 men into a duel: at 7.5 m that is a 47 m
 * circumference and one man per metre, shoulder to shoulder, which is a siege
 * line and not a duelling ring. The nearest `RING_MAX` are staged and the rest
 * are left to the battle they were already in — a ring the player can see past
 * is the whole point of clearing the floor.
 */
export const RING_MAX = 16;
/**
 * How much faster the ring moves while it is forming.
 *
 * Measured: in a live battle the ring formed in 1083-1433 ms, but those duels
 * began with 3 to 15 of the men already at ring distance. Staged from a dense
 * knot — the hero standing in the middle of a press, all sixteen men inside the
 * band and every one of them needing to walk OUT — only 3 of 16 had arrived
 * after 1.6 s. A staged beat is allowed to move people; `seek` already scales
 * by `spdMul`, so this is one field, restored with the rest.
 */
export const RING_HASTE = 1.9;
/** The prompt stays offered this long before the moment passes. */
export const PROMPT_HOLD = 6;
/** The same officer will not prompt again this soon after one ends. */
export const RECHALLENGE = 10;
/** Separate by more than this and the duel is broken off. */
export const LEASH = 24;
/** A duel cannot run forever. */
export const MAX_SECONDS = 90;

export function createDuel() {
  let W = null;
  let state = 'idle';            // idle | prompt | active
  let rec = null;                // the officer record under offer or fight
  let promptT = 0, activeT = 0, ringMs = -1;
  /** The last officer uid seen engaged, so an encounter is counted once. */
  let lastSeen = -1;
  let mounted = false, root = null, styleEl = null;
  const R = {};
  const S = { show: 0 };
  /** Saved `ringR` and `spdMul` per entity id, restored when the duel ends. */
  const saved = new Map();
  const savedSpd = new Map();
  /**
   * Who was ALREADY standing at ring distance when the duel began.
   *
   * Without this, "the ring formed in 17 ms" is a true statement about a
   * measurement and a false one about the game: the gather range is 16 m and
   * the ring band is 5.3-9.7 m, so four of the twelve men pulled in are
   * usually inside the band already and the count clears RING_MIN on the first
   * frame. What the gate is actually asking is how fast the men who had to
   * MOVE get there.
   */
  const startedOut = new Set();
  /** Officers recently fought, by uid, with the sim time they ended. */
  const recent = new Map();

  const stats = {
    encounters: 0, prompts: 0, accepted: 0, declined: 0, expired: 0,
    wins: 0, losses: 0, brokenOff: 0,
    ringFormedMs: [], ringMen: [], lastRingMs: -1, ringArrived: [], ringOutAtStart: [],
  };

  /* ------------------------------------------------------------------ ring */

  function duelCentre(out) {
    const e = W.entities, p = W.playerId, o = rec.id;
    out.x = (e.x[p] + e.x[o]) * 0.5;
    out.z = (e.z[p] + e.z[o]) * 0.5;
    return out;
  }
  const _c = { x: 0, z: 0 };

  /** Push the nearby crowd out to the ring. Returns how many were moved. */
  function openTheFloor() {
    const e = W.entities, A = W.ai && W.ai.store;
    if (!A) return 0;
    duelCentre(_c);
    const g2 = GATHER_RANGE * GATHER_RANGE;
    let n = 0;
    // nearest first, so a capped ring is the men who were closest to the duel
    _cand.length = 0;
    for (let i = 0; i < e.liveN; i++) {
      const id = e.live[i];
      if (id === W.playerId || id === rec.id) continue;
      if (e.state[id] === ST.DEAD) continue;
      if (e.kind[id] === KIND.OFFICER || e.kind[id] === KIND.GENERAL) continue;
      /* ENEMIES ONLY. ACT.RING orbits a unit's TARGET, so putting an ALLY in
       * the ring would mean setting its target to the player — the hero's own
       * men would ring him as an enemy. Allies keep their own business and
       * their own spacing; the floor is opened by moving the side that was
       * trying to swarm him. */
      if (e.team[id] !== TEAM.ENEMY) continue;
      const dx = e.x[id] - _c.x, dz = e.z[id] - _c.z;
      const d2 = dx * dx + dz * dz;
      if (d2 > g2) continue;
      _cand.push(id, d2);
    }
    // selection by distance, capped: no allocation beyond the flat pair list
    const take = Math.min(RING_MAX, _cand.length / 2);
    for (let k = 0; k < take; k++) {
      let bi = -1, bd = 1e18;
      for (let c = 0; c < _cand.length; c += 2) {
        if (_cand[c] < 0) continue;
        if (_cand[c + 1] < bd) { bd = _cand[c + 1]; bi = c; }
      }
      if (bi < 0) break;
      const id = _cand[bi];
      _cand[bi] = -1;
      if (!saved.has(id)) saved.set(id, A.ringR[id]);
      if (!savedSpd.has(id)) savedSpd.set(id, A.spdMul[id]);
      A.spdMul[id] = A.spdMul[id] * RING_HASTE;
      const d0 = Math.sqrt(bd);
      if (Math.abs(d0 - RING_RADIUS) > RING_TOLERANCE) startedOut.add(id);
      A.ringR[id] = RING_RADIUS;
      // Point them at the duel and put them in the ring: ACT.RING seeks a
      // circle around the unit's TARGET, so the target is what centres it.
      A.tgt[id] = W.playerId;
      A.act[id] = ACT.RING; A.actT[id] = 0;
      n++;
    }
    return n;
  }
  const _cand = [];

  /**
   * How many of the ring are standing where a ring should stand, split by
   * whether they had to travel to do it.
   */
  function ringCount(out) {
    const e = W.entities;
    duelCentre(_c);
    let n = 0, moved = 0, liveOut = 0;
    for (const id of saved.keys()) {
      if (e.state[id] === ST.DEAD) continue;
      const dx = e.x[id] - _c.x, dz = e.z[id] - _c.z;
      const inBand = Math.abs(Math.hypot(dx, dz) - RING_RADIUS) <= RING_TOLERANCE;
      if (inBand) n++;
      if (startedOut.has(id)) { liveOut++; if (inBand) moved++; }
    }
    if (out) { out.inBand = n; out.arrived = moved; out.hadToMove = liveOut; }
    return n;
  }
  const _rc = { inBand: 0, arrived: 0, hadToMove: 0 };

  function closeTheFloor() {
    const A = W.ai && W.ai.store;
    if (A) {
      for (const [id, r] of saved) A.ringR[id] = r;
      for (const [id, v] of savedSpd) A.spdMul[id] = v;
    }
    saved.clear();
    savedSpd.clear();
    startedOut.clear();
  }

  /* ----------------------------------------------------------- transitions */

  function offer(r) {
    rec = r; state = 'prompt'; promptT = 0;
    stats.prompts++;
    W.ui?.toast?.(`${r.name} bars the way — press C to answer`);
  }

  function accept() {
    if (state !== 'prompt' || !rec) return false;
    state = 'active'; activeT = 0; ringMs = -1;
    stats.accepted++;
    const men = openTheFloor();
    stats.ringMen.push(men);
    stats.ringOutAtStart.push(startedOut.size);
    W.camrig?.cinematic?.('duel');
    W.ui?.banner?.('DUEL', `${rec.name} · ${rec.title || ''}`.trim(), 2.2);
    const e = W.entities, id = W.playerId;
    W.audio?.play?.('voice_shout', { x: e.x[id], y: e.y[id] + 1.6, z: e.z[id], gain: 1.0 });
    return true;
  }

  function decline() {
    if (state !== 'prompt') return false;
    stats.declined++;
    finish('declined');
    return true;
  }

  /** End the duel, whatever the reason, and always put the crowd back. */
  function finish(why) {
    if (rec) recent.set(rec.uid, W.time ? W.time.sim : 0);
    if (state === 'active') {
      closeTheFloor();
      if (why === 'win') {
        stats.wins++;
        /* The consequence. A duel is the hardest thing on the field, so it
         * pays the resource the rest of the game spends: a full bravery pool,
         * which is three charges or a Battle Art, immediately. */
        const p = W.player;
        if (p && p.gainBravery) p.gainBravery(p.maxBravery);
        W.ui?.banner?.('VICTOR', rec ? `${rec.name} has fallen` : '', 2.4);
      } else if (why === 'loss') {
        stats.losses++;
      } else if (why === 'broken') {
        stats.brokenOff++;
        W.ui?.toast?.('The duel is broken off');
      }
    } else if (why === 'expired') {
      stats.expired++;
    }
    state = 'idle'; rec = null; promptT = 0; activeT = 0;
  }

  /* -------------------------------------------------------------------- ui */

  const CSS = `
.sb .duel{position:absolute; left:50%; top:calc(var(--u)*210); width:calc(var(--u)*420);
  margin-left:calc(var(--u)*-210); opacity:0; text-align:center; pointer-events:none}
.sb .duel .dn{font-size:calc(var(--u)*22); letter-spacing:calc(var(--u)*3);
  color:#ffeaba; line-height:1;
  text-shadow:0 calc(var(--u)*3) calc(var(--u)*14) rgba(0,0,0,.95), 0 0 calc(var(--u)*28) rgba(255,150,60,.5)}
.sb .duel .dk{margin-top:calc(var(--u)*7); font-size:calc(var(--u)*10.5); font-weight:700;
  letter-spacing:calc(var(--u)*3.4); text-transform:uppercase; color:rgba(255,226,170,.9)}
.sb .duel .dk b{color:#0e0b07; background:linear-gradient(180deg,#ffe6ac,#d9a44a);
  padding:0 calc(var(--u)*6); margin:0 calc(var(--u)*3)}
`;

  function mount(w) {
    if (mounted) return true;
    const host = w.ui && w.ui.root;
    if (!host || !host.appendChild) return false;
    styleEl = document.createElement('style');
    styleEl.textContent = CSS;
    host.appendChild(styleEl);
    root = document.createElement('div');
    root.className = 'duel';
    root.innerHTML = '<div class="dn ser"></div><div class="dk"></div>';
    host.appendChild(root);
    R.name = root.querySelector('.dn');
    R.key = root.querySelector('.dk');
    mounted = true;
    return true;
  }

  return {
    name: 'duel', order: ORDER.MISSION,

    init(w) {
      W = w;
      w.duel = {
        accept, decline,
        /**
         * Offer a duel with a named officer now, bypassing the proximity
         * watch. A mission script wanting a scripted challenge needs this, and
         * so does anything testing the ring without waiting for an encounter
         * to happen by itself.
         */
        challenge(r) {
          if (state !== 'idle' || !r || !r.alive) return false;
          offer(r);
          return true;
        },
        get state() { return state; },
        get officer() { return rec; },
        stats,
        info() {
          return {
            state, officer: rec ? rec.name : null,
            ringMen: saved.size, ringNow: state === 'active' ? ringCount(_rc) : 0,
            ringArrived: _rc.arrived, ringHadToMove: _rc.hadToMove,
            ringFormedMs: stats.lastRingMs,
            encounters: stats.encounters, prompts: stats.prompts,
            accepted: stats.accepted, wins: stats.wins, losses: stats.losses,
            brokenOff: stats.brokenOff, expired: stats.expired,
          };
        },
        reset() {
          if (state === 'active') closeTheFloor();
          state = 'idle'; rec = null; recent.clear();
          stats.encounters = 0; stats.prompts = 0; stats.accepted = 0;
          stats.declined = 0; stats.expired = 0; stats.wins = 0;
          stats.losses = 0; stats.brokenOff = 0;
          stats.ringFormedMs.length = 0; stats.ringMen.length = 0;
          stats.ringArrived.length = 0; stats.ringOutAtStart.length = 0;
          stats.lastRingMs = -1;
        },
        constants: { PROMPT_RANGE, GATHER_RANGE, RING_RADIUS, RING_TOLERANCE, RING_MIN, RING_MAX, RING_HASTE },
      };

      // The officer falling is the win condition, and the AI already announces
      // it. No polling, and no second definition of "he is dead".
      w.bus.on('officer:down', (id) => {
        if (state === 'active' && rec && rec.id === id) finish('win');
      });
      w.bus.on('kill', (victim) => {
        if (state === 'active' && victim === w.playerId) finish('loss');
      });
      w.bus.on('mission:start', () => { w.duel.reset(); });
    },

    fixedUpdate(w, dt) {
      const m = w.mission;
      if (!m || m.state !== 'active' || m.paused) {
        if (state !== 'idle') finish('broken');
        return;
      }
      const e = w.entities, now = w.time ? w.time.sim : 0;

      if (state === 'active') {
        activeT += dt;
        if (ringMs < 0) {
          ringCount(_rc);
          /* FORMED means the men who had to travel have arrived, not merely
           * that RING_MIN bodies are at the right distance — several usually
           * are already. When nobody had to move, the ring was already there
           * and the honest answer is 0, not a frame time. */
          const travelled = _rc.hadToMove === 0 || _rc.arrived >= Math.ceil(_rc.hadToMove * 0.6);
          if (_rc.inBand >= RING_MIN && travelled) {
            ringMs = Math.round(activeT * 1000);
            stats.ringFormedMs.push(ringMs);
            stats.lastRingMs = ringMs;
            stats.ringArrived.push({ inBand: _rc.inBand, arrived: _rc.arrived, hadToMove: _rc.hadToMove });
          }
        }
        // ended by distance, death or patience
        if (!rec || !rec.alive || e.state[rec.id] === ST.DEAD) { finish('win'); return; }
        const dx = e.x[rec.id] - e.x[w.playerId], dz = e.z[rec.id] - e.z[w.playerId];
        if (Math.hypot(dx, dz) > LEASH) { finish('broken'); return; }
        if (activeT > MAX_SECONDS) { finish('broken'); return; }
        return;
      }

      if (state === 'prompt') {
        promptT += dt;
        if (!rec || !rec.alive || rec.dist > PROMPT_RANGE * 1.4) { finish('expired'); return; }
        if (promptT > PROMPT_HOLD) { finish('expired'); return; }
        if (w.input && w.input.consume && w.input.consume('duel', 0.3)) accept();
        return;
      }

      /* idle: watch for an encounter. `world.ai.engaged` is maintained by the
       * AI and already means "targeting the player, inside 16 m", so an
       * ENCOUNTER is simply a new officer appearing there. Counting it here —
       * rather than counting prompts — is what lets the gate ask what share of
       * encounters become duels. */
      const eng = w.ai && w.ai.engaged;
      if (!eng || !eng.alive) return;
      if (lastSeen !== eng.uid) { lastSeen = eng.uid; stats.encounters++; }
      const last = recent.get(eng.uid);
      if (last !== undefined && now - last < RECHALLENGE) return;
      if (eng.dist > PROMPT_RANGE) return;
      offer(eng);
    },

    update(w, dt, alpha, realDt) {
      if (!mounted && !mount(w)) return;
      const k = Math.min(0.1, realDt > 0 ? realDt : 1 / 60);
      const want = state === 'prompt' ? 1 : 0;
      S.show += (want - S.show) * Math.min(1, k * 9);
      const v = S.show < 0 ? 0 : S.show > 1 ? 1 : S.show;
      if (root.__o === undefined || Math.abs(root.__o - v) > 0.004) {
        root.__o = v; root.style.opacity = v.toFixed(3);
      }
      if (v < 0.004 || !rec) return;
      if (R.name.__t !== rec.name) { R.name.__t = rec.name; R.name.textContent = rec.name; }
      if (R.key.__t === undefined) {
        R.key.__t = 1;
        R.key.innerHTML = 'Answer the challenge <b>C</b>';
      }
    },

    dispose() {
      root && root.remove(); styleEl && styleEl.remove();
      mounted = false;
    },
  };
}
